/**
 * Tenant Service
 *
 * Module-level functions for reading and writing tenants. Each function calls
 * the Go API via erebusHttp (cookie-authenticated) and returns the response
 * data. Errors pass through untouched — callers handle them.
 *
 * Calls can fail with 409 for two different reasons. Use the helpers at the
 * bottom of this file to tell them apart:
 * - isTenantSyncManagedError: the migration state refuses the call. Reads are
 *   refused before soaking, writes before cutover.
 * - isTenantCodeConflictError: the tenant code is already used (codes are
 *   never reused, not even by a deleted tenant)
 *
 * A logo upload can also get 409 `conflict`, but there it means another upload
 * changed the same logo at the same moment. Use isTenantLogoRaceError for it.
 */

import { erebusHttp } from './instances';
import type {
  CreateTenantRequest,
  Tenant,
  TenantLogoType,
  TenantStatus,
  UpdateTenantRequest,
} from './types';

/** Returns the tenants the caller can see, sorted by name. Optionally filtered by status. */
export async function listTenants(params?: { status?: 'active' | 'inactive' }): Promise<Tenant[]> {
  const { data } = await erebusHttp.get<Tenant[]>('/tenants', {
    ...(params?.status && { params: { status: params.status } }),
  });
  return data;
}

/** Returns one tenant by its legacy client ID. 404 when missing or the caller has no role at it. */
export async function getTenant(legacyClientId: number): Promise<Tenant> {
  const { data } = await erebusHttp.get<Tenant>(`/tenants/${legacyClientId}`);
  return data;
}

/** Creates an active tenant. Global admins only. 409 when the code is used or writes are refused. */
export async function createTenant(request: CreateTenantRequest): Promise<Tenant> {
  const { data } = await erebusHttp.post<Tenant>('/tenants', request);
  return data;
}

/** Changes only the fields sent. Status is not changed here — use setTenantStatus. */
export async function updateTenant(
  legacyClientId: number,
  patch: UpdateTenantRequest,
): Promise<Tenant> {
  const { data } = await erebusHttp.patch<Tenant>(`/tenants/${legacyClientId}`, patch);
  return data;
}

/** Sets a tenant to active or inactive. Tenants cannot be deleted through the API. */
export async function setTenantStatus(
  legacyClientId: number,
  status: TenantStatus,
): Promise<Tenant> {
  const { data } = await erebusHttp.put<Tenant>(`/tenants/${legacyClientId}/status`, { status });
  return data;
}

/**
 * Uploads a tenant's logo or mobile logo, replacing the current one. The file
 * must be a png, jpeg or gif of 10MB or less (the backend checks the file
 * itself). Returns the tenant with fresh logo URLs.
 */
export async function setTenantLogo(
  legacyClientId: number,
  type: TenantLogoType,
  file: File,
): Promise<Tenant> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await erebusHttp.put<Tenant>(`/tenants/${legacyClientId}/logos/${type}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Removes a tenant's logo or mobile logo. 404 when the tenant has no logo of that type. */
export async function removeTenantLogo(
  legacyClientId: number,
  type: TenantLogoType,
): Promise<void> {
  await erebusHttp.delete(`/tenants/${legacyClientId}/logos/${type}`);
}

// =============================================================================
// 409 helpers
// =============================================================================

/** Reads the Go error payload's `code` from a 409 response, or undefined. */
function conflictCode(err: unknown): string | undefined {
  const response = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)
    ?.response;
  if (response?.status !== 409) return undefined;
  const code = response.data?.code;
  return typeof code === 'string' ? code : undefined;
}

/** True when the migration state refused a tenant read or write (409 `sync_managed`). */
export function isTenantSyncManagedError(err: unknown): boolean {
  return conflictCode(err) === 'sync_managed';
}

/** True when the tenant code is already used by another tenant, live or deleted (409 `conflict`). */
export function isTenantCodeConflictError(err: unknown): boolean {
  return conflictCode(err) === 'conflict';
}

/** True when a logo upload lost a race with another change to the same logo (409 `conflict`). */
export function isTenantLogoRaceError(err: unknown): boolean {
  return conflictCode(err) === 'conflict';
}
